<template>
  <DropdownMenu v-if="auth.isAuthenticated">
    <DropdownMenuTrigger as-child>
      <!-- ⭐ THE ACCOUNT CLUSTER — the organization and the user in ONE control,
           because the menu behind it was already both: org settings, « changer
           d'organisation », then the person's own settings and logout. The org's
           own logo finally appears in the console (it has been in the database
           all along — `has_one_attached :logo` — and only the public proposal
           and the admin ever showed it).
           Both marks are ROUND (his call): the org and the person are told apart
           by the NAME sitting between them, not by a corner radius.
           The logo is cropped to a circle at upload (LogoUploader), so it fills
           the disc here — a wide logo contained in a round tile read as a thin
           strip floating in a hole (his QA). -->
      <Button variant="ghost" class="h-11 gap-2.5 rounded-full pl-1.5 pr-1" data-testid="account-menu">
        <OrgAvatar
          v-if="orgStore.currentOrganization"
          :org="orgStore.currentOrganization"
          class="h-9 w-9 shrink-0 text-xs"
          data-testid="account-org-logo"
        />
        <!-- The name is the tenant signal — which organization am I about to
             create this lead in. It yields first on a narrow window; the square
             and the person never do. -->
        <span
          v-if="orgName"
          class="hidden min-w-0 max-w-52 truncate text-sm font-semibold lg:inline"
          data-testid="account-org-name"
        >{{ orgName }}</span>
        <Avatar class="h-9 w-9">
          <AvatarImage v-if="avatarUrl" :src="avatarUrl" :alt="auth.currentUser.attributes.email" />
          <AvatarFallback>{{ initials }}</AvatarFallback>
        </Avatar>
      </Button>
    </DropdownMenuTrigger>
    <!-- Wide enough for a real company name: a tenant switcher must never
         truncate the one word that tells two shops apart. -->
    <DropdownMenuContent class="w-80" align="end">
      <DropdownMenuLabel class="font-normal">
        <div class="flex flex-col space-y-1">
          <p class="text-sm font-medium leading-none">{{ fullName }}</p>
          <p class="text-xs leading-none text-muted-foreground">{{ auth.currentUser.attributes.email }}</p>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      
      <DropdownMenuGroup>
        <DropdownMenuItem @click="router.push('/settings')">
          <UserIcon class="mr-2 h-4 w-4" />
          <span>{{ t('settings.title') }}</span>
        </DropdownMenuItem>

        <DropdownMenuItem v-if="orgStore.currentOrganization && isAdmin" @click="router.push('/organizations/settings')">
          <BuildingIcon class="mr-2 h-4 w-4" />
          <span>{{ t('organizations.settings') }}</span>
        </DropdownMenuItem>

        <!-- Platform operator only (self-serialized flag — absent for everyone
             else). Full navigation: the panel lives on the admin. host. -->
        <DropdownMenuItem v-if="auth.currentUser.attributes.platform_admin" @click="goAdmin">
          <ShieldIcon class="mr-2 h-4 w-4" />
          <span>{{ t('admin.title') }}</span>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      
      <DropdownMenuSeparator />
      
      <DropdownMenuGroup v-if="orgStore.organizations.length > 1">
        <DropdownMenuLabel class="text-xs font-normal text-muted-foreground">
          {{ t('organizations.switch') }}
        </DropdownMenuLabel>
        <DropdownMenuItem
          v-for="org in otherOrganizations"
          :key="org.id"
          class="gap-2.5"
          @click="switchOrganization(org.id)"
        >
          <OrgAvatar :org="org" class="h-7 w-7 shrink-0 text-[10px]" />
          <!-- Wraps, never truncates (see the width note above). -->
          <span class="min-w-0 break-words leading-snug">{{ org.attributes.name }}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
      </DropdownMenuGroup>

      <!-- The theme belongs HERE, not in permanent chrome: it is a preference
           pressed twice a year, and it was standing in the header beside the two
           most-used controls in the app. `@select.prevent` keeps the menu open
           so you see the page change under it — the one case where a menu item
           should not close.
           ⚠ Its label used to be a hard-coded « Toggle theme » in a sr-only
           span, which is exactly the string the i18n law forbids. -->
      <DropdownMenuItem data-testid="theme-toggle" @select.prevent="toggleTheme">
        <SunIcon v-if="isDark" class="mr-2 h-4 w-4" />
        <MoonIcon v-else class="mr-2 h-4 w-4" />
        <span>{{ isDark ? t('common.theme.light') : t('common.theme.dark') }}</span>
      </DropdownMenuItem>

      <DropdownMenuItem @click="handleLogout">
        <LogOutIcon class="mr-2 h-4 w-4" />
        <span>{{ t('auth.logout') }}</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useOrganizationStore } from '@/stores/organization'
import { usePermissions } from '@/composables/usePermissions'
import { useAppShell } from '@/composables/useAppShell'
import { useOrganizationSwitch } from '@/composables/useOrganizationSwitch'
import { mediaUrl } from '@/lib/utils'
import OrgAvatar from '@/components/shared/OrgAvatar.vue'
import {
  User as UserIcon,
  Building as BuildingIcon,
  Shield as ShieldIcon,
  LogOut as LogOutIcon,
  Sun as SunIcon,
  Moon as MoonIcon
} from 'lucide-vue-next'
import { adminOrigin } from '@/lib/host'

import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const orgStore = useOrganizationStore()
const { isAdmin } = usePermissions()
const { isDark, toggleTheme } = useAppShell()
const { switchOrganization } = useOrganizationSwitch()

// A member keeps the door: the server lets them at periods, the composer, taxes,
// sectors and imports. The admin-gated tabs are hidden from them and « Général »
// is one, so a member lands on the first tab they can use.

const handleLogout = async () => {
  await auth.logout()
}

const goAdmin = () => {
  window.location.href = adminOrigin()
}

const fullName = computed(() => {
  const { first_name, last_name, email } = auth.currentUser.attributes
  if (first_name && last_name) return `${first_name} ${last_name}`
  return email
})

const initials = computed(() => {
  const { first_name, last_name, email } = auth.currentUser.attributes
  if (first_name && last_name) {
    return `${first_name.charAt(0)}${last_name.charAt(0)}`.toUpperCase()
  }
  return email?.charAt(0).toUpperCase() || 'U'
})

const avatarUrl = computed(() => mediaUrl(auth.currentUser.attributes.avatar_url))

// The ORG's name — the tenant signal in the trigger.
const orgName = computed(() => orgStore.currentOrganization?.attributes.name ?? '')

const otherOrganizations = computed(() => {
  return orgStore.organizations.filter(org => org.id !== orgStore.currentOrganizationId)
})
</script>
